import { UNNAMED } from '@game/constants';
import type {
  ClientArenaSearchResult,
  ClientCooldown,
  ClientEffect,
  ClientEquipmentItem,
  ClientInventoryItem,
  ClientLocation,
  ClientMissionRecord,
  ClientNotificationRecord,
  ClientRewardItem,
  ClientSkill,
  ClientTrait,
  CompleteMissionRecord,
  SkillLearnChanceAddition,
} from '@game/shared-types';

import type { MappedEquipmentItem } from '../types/inventory.types';
import { defaultLocation } from './userState';

interface EventsCountersState {
  jobsPerformed: number;
  jobsEarned: number;
  mailsSent: number;
  creditsSpent: number;
  chatMessagesSent: number;
  workoutsPerformed: number;
  itemsBought: number;
  itemsConsumed: number;
  itemsRemoved: number;
  itemsSent: number;
  healsBought: number;
  timesOverdosed: number;
  timeOnline: number;
  creditsEarnedFromSellingItems: number;
  combatsFought: number;
  combatsWon: number;
  combatsLost: number;
  itemsCraftedFailures: number;
  itemsCraftedSuccesses: number;
  itemsCrafted: number;
  learnSkillFailures: number;
  learnSkillSuccesses: number;
  missionsCompleted: number;
  creditsEarnedFromMissions: number;
  workoutCounters: { [key: string]: number };
}

interface StatsState {
  level: number;
  lastLevel: number;
  credits: number;
  healthCurrent: number;
  healthMax: number;
  energyCurrent: number;
  energyMax: number;
  spiritCurrent: number;
  spiritMax: number;
  expCurrent: number;
  expMax: number;
  newMail: number;
  newNotifications: number;
  workoutStats: { [key: string]: number };
  isOverdosed: boolean;
}

export interface PlayerState extends StatsState {
  id: string;
  name: string;
  avatar: string;
  tags: string[];
  avatarUrl: string;
  energyNextTick: number;
  spiritNextTick: number;
  inventory: ClientInventoryItem[];
  equipment: ClientEquipmentItem[];
  mappedEquipment: MappedEquipmentItem[];
  notifications: ClientNotificationRecord[];
  activeMissions: ClientMissionRecord[];
  completeMissions: ClientMissionRecord[];
  completeMissionsRecords: CompleteMissionRecord[];
  buyHealingAmount?: number;
  searchArenaResult: ClientArenaSearchResult[] | null;
  searchingArena?: boolean;
  searchArenaResultText: string | null;
  learningSkill?: boolean;
  learnSkillResult?: boolean;
  learnSkillName?: string;
  jobEarnAmount?: number;
  jobRewards?: ClientRewardItem[];
  workingOut?: boolean;
  workoutEarnAmount?: number;
  workoutId?: string;
  effects: ClientEffect[];
  cooldowns: ClientCooldown[];
  biography: string;
  skills: ClientSkill[];
  equippedSkills: ClientSkill[];
  traits: ClientTrait[];
  skillLearnChanceAdditions: SkillLearnChanceAddition[];
  locationLastEngagedAt: ClientLocation | null;
  eventCounters: EventsCountersState;
  craftingSchematics: string[];
}

const eventCountersDefault = {
  jobsPerformed: 0,
  jobsEarned: 0,
  mailsSent: 0,
  creditsSpent: 0,
  chatMessagesSent: 0,
  workoutsPerformed: 0,
  itemsBought: 0,
  itemsConsumed: 0,
  itemsRemoved: 0,
  itemsSent: 0,
  healsBought: 0,
  timesOverdosed: 0,
  timeOnline: 0,
  creditsEarnedFromSellingItems: 0,
  combatsFought: 0,
  combatsWon: 0,
  combatsLost: 0,
  itemsCraftedFailures: 0,
  itemsCraftedSuccesses: 0,
  itemsCrafted: 0,
  learnSkillFailures: 0,
  learnSkillSuccesses: 0,
  missionsCompleted: 0,
  creditsEarnedFromMissions: 0,
  workoutCounters: {},
};

const statsDefault: StatsState = {
  level: 0,
  lastLevel: 0,
  credits: 0,
  healthCurrent: 0,
  healthMax: 0,
  energyCurrent: 0,
  energyMax: 0,
  spiritCurrent: 0,
  spiritMax: 0,
  expCurrent: 0,
  expMax: 0,
  newMail: 0,
  newNotifications: 0,
  workoutStats: {},
  isOverdosed: false,
};

export const playerDefault: PlayerState = {
  name: UNNAMED,
  id: '',
  avatar: '',
  avatarUrl: '',
  tags: [],
  energyNextTick: 0,
  spiritNextTick: 0,
  inventory: [],
  equipment: [],
  mappedEquipment: [],
  notifications: [],
  activeMissions: [],
  completeMissions: [],
  completeMissionsRecords: [],
  searchingArena: false,
  effects: [],
  cooldowns: [],
  biography: '',
  skills: [],
  equippedSkills: [],
  traits: [],
  skillLearnChanceAdditions: [],
  locationLastEngagedAt: defaultLocation,
  eventCounters: eventCountersDefault,
  searchArenaResult: null,
  searchArenaResultText: null,
  craftingSchematics: [],
  ...statsDefault,
};
